import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type {
  TacticsCoachingPoint,
  TacticsLayout,
  TacticsZone,
  TacticsDrawing as TacticsLayoutDrawings,
} from '@4cm/core/tactics';
import { mirrorTacticsLayout } from '@4cm/core/tactics/mirror';
import { inferRoleAtPosition } from '@4cm/core/tactics/positionRoles';
import {
  appendBriefHistory,
  generateAllPlayerBriefs,
  type PlayerBrief,
} from '@4cm/core/tactics/playerBriefs';
import { applyTeamShapeTransform } from '@4cm/core/tactics/teamShape';
import { polishAllPlayerBriefs } from '../../lib/playerBriefPolish';
import { computeTeamCompactness } from '@4cm/core/tactics/compactness';
import { encodeTacticCode, decodeTacticCode } from '@4cm/core/tactics/tacticCodes';
import {
  defaultOverlaysState,
  computeVoronoiCells,
  computePassNetwork,
  computeTeamShapeBands,
  computePitchControl,
  computeHeatmap,
  computeOffsideLines,
  computeDefensiveShape,
  type TacticsOverlaysState,
} from '@4cm/core/tactics/overlays';
import {
  defaultBundle,
  type TeamTacticsBundle,
} from '@4cm/core/tactics/teamTactics';
import {
  defaultDrawing,
  type ArrowHead,
  type DashStyle,
  type DrawingKind,
  type TacticsDrawing,
} from '@4cm/core/tactics/drawings';
import type { PlayerRoleSelection } from '@4cm/core/tactics/roles';
import {
  SET_PIECE_TYPES,
  defaultSetPiece,
  fieldRegionForSetPiece,
  type FieldRegion,
  type SetPieceType,
  type TacticsSetPiece,
} from '@4cm/core/tactics/setPieces';
import { CORNER_TEMPLATES } from '@4cm/core/tactics/cornerTemplates';
import {
  EXTENDED_FORMATIONS,
  mirrorFormationForAway,
} from '@4cm/core/tactics/formationsExtended';
import { getSetPiece, saveSetPiece } from '../../lib/setPiecesStore';
import { listCustomFormations, saveCustomFormation } from '../../lib/customFormationsStore';
import { useTacticsHistory } from '../../lib/useTacticsHistory';
import { useWalkthroughPlayback } from '../../lib/useWalkthroughPlayback';
import {
  DEFAULT_FORMATION,
  FIELD_LENGTH,
  FIELD_WIDTH,
  projectFormation,
  SUPPORTED_FORMATIONS,
  type PlayerSpot,
} from '../tactics3d/formationProjection';
import {
  clearTacticsLayout,
  loadTacticsLayout,
  onTacticsLayoutChanged,
  saveTacticsLayout,
} from '../../lib/tactics3dStore';
import { getPattern, savePattern } from '../../lib/tacticsPatternsStore';
import { ArrowDrawLayer2D } from './ArrowDrawLayer2D';
import { DrawingLayer2D } from './DrawingLayer2D';
import { MovementArrow2D, type MovementArrow2DSpec } from './MovementArrow2D';
import { PitchSvg } from './PitchSvg';
import { PlayerToken2D } from './PlayerToken2D';
import { rosterIdentityFor } from '../../lib/roster';
import { loadLocalPlayers } from '../../lib/demoData';
import { VoronoiLayer2D } from './VoronoiLayer2D';
import { HalfSpacesLayer2D } from './HalfSpacesLayer2D';
import { TeamShapeLayer2D } from './TeamShapeLayer2D';
import { PositionalPlayGridLayer2D } from './PositionalPlayGridLayer2D';
import { XTLayer2D } from './XTLayer2D';
import { ZonePolygonOverlay } from './ZonePolygonOverlay';
import { PassNetLayer2D } from './PassNetLayer2D';
import { PitchControlLayer2D } from './PitchControlLayer2D';
import { HeatmapLayer2D } from './HeatmapLayer2D';
import { OffsideLineLayer2D } from './OffsideLineLayer2D';
import { DefensiveShapeLayer2D } from './DefensiveShapeLayer2D';
import { ZoneLayer2D } from './ZoneLayer2D';
import {
  DrawingToolbar,
  FormationDropdown,
  FormationLibraryDrawer,
  HudChip,
  HudHint,
  HudRow,
  HudShell,
  SurfacePills,
  OverlayToggles,
  RoleInstructionsPicker,
  RunPlaybackControls,
  SavedAgo,
  SetPieceDrawer,
  TeamTacticsPanel,
  WalkthroughControls,
  useReducedMotion,
  CompactnessChips,
  PositionSlotPicker,
  PlayerBriefsDrawer,
  TacticMenu,
  LibraryMenu,
} from '../tacticsHud';
import { CoachingPointPin } from '../tacticsHud/CoachingPointPin';
import { PatternDrawer } from '../tacticsHud/PatternDrawer';
import './TacticsBoard2D.css';

/**
 * 2D top-down tactics surface — the SVG counterpart to TacticsBoard3D.
 *
 * Reads + writes the same `4cm_local_tactics_3d` localStorage entry
 * via `apps/web2/src/lib/tactics3dStore.ts`, so a layout drawn in 2D
 * appears in 3D and vice versa under the same `persistKey`.
 *
 * Coordinate system: world metres, pitch-centred (the SVG's viewBox
 * is `-W/2..W/2 × -L/2..L/2`). Positions and arrows passed through
 * children components in metres directly — no pixel projection.
 *
 * HUD parity with the 3D board:
 * - Mode toggle (Move / Draw arrow), formation chips for home + away,
 *   Reset, Runs row with Play/Pause/Stop/scrubber.
 * - 2D-only View row replaces the 3D camera presets — flips the SVG
 *   to "rotated" or "side" reads for variety. Camera presets do not
 *   apply in flat 2D.
 * - Phase 4 — DESIGN row with Formations / Tactics / Set pieces /
 *   Overlays / Roles / Draw chips, plus Undo / Redo. The overlay
 *   chips reveal an inline OverlayToggles strip below the HUD row.
 */

const X_LIMIT = FIELD_WIDTH / 2 - 1;
const Z_LIMIT = FIELD_LENGTH / 2 - 1;
function clampToPitch(spot: PlayerSpot, x: number, z: number): PlayerSpot {
  return {
    ...spot,
    x: Math.max(-X_LIMIT, Math.min(X_LIMIT, x)),
    z: Math.max(-Z_LIMIT, Math.min(Z_LIMIT, z)),
  };
}

/**
 * 2D-only view modes. The board ships LANDSCAPE by default (length on
 * the horizontal axis, home defending end on the left). View presets
 * reframe the same world coordinates without re-projecting:
 *
 * - `top`: landscape, home end on the left (default — broadcast read).
 * - `flipped`: 180° rotation, home end on the right (useful when the
 *    coach is diagramming the opponent's half).
 * - `side`: 90° CCW rotation back to a portrait read (classic chalk
 *    board with home at the bottom).
 */
const VIEW_PRESETS = [
  { id: 'top', label: 'Top-down' },
  { id: 'flipped', label: 'Flipped' },
  { id: 'side', label: 'Side' },
] as const;
type ViewPresetId = (typeof VIEW_PRESETS)[number]['id'];
const DEFAULT_VIEW: ViewPresetId = 'top';

// View rotation is driven entirely by CSS off the `data-view` attribute
// on `.t2d-stage` (see TacticsBoard2D.css) — one source of truth, so it
// no longer fights the old responsive auto-rotation.

// Linear interpolation between two world-XZ points along an array of
// control points, parameterised by `t ∈ [0, 1]`. Matches the 3D
// board's run-path playback behaviour (we use Catmull-Rom-equivalent
// smoothing in the path render but step the playback head through
// a piecewise-linear sampling of the curve, which is what the 3D
// board does via THREE.CatmullRomCurve3.getPointAt).
function linearArcLengthSample(
  points: Array<{ x: number; z: number }>,
  t: number,
): { x: number; z: number } {
  if (points.length === 0) return { x: 0, z: 0 };
  if (points.length === 1 || t <= 0) return points[0];
  if (t >= 1) return points[points.length - 1];
  // Cumulative arc lengths.
  const cum: number[] = [0];
  for (let i = 1; i < points.length; i++) {
    const dx = points[i].x - points[i - 1].x;
    const dz = points[i].z - points[i - 1].z;
    cum.push(cum[i - 1] + Math.hypot(dx, dz));
  }
  const total = cum[cum.length - 1];
  if (total === 0) return points[0];
  const target = total * t;
  // Find the segment containing target.
  let i = 1;
  while (i < cum.length && cum[i] < target) i++;
  const segT = (target - cum[i - 1]) / (cum[i] - cum[i - 1] || 1);
  const a = points[i - 1];
  const b = points[i];
  return {
    x: a.x + (b.x - a.x) * segT,
    z: a.z + (b.z - a.z) * segT,
  };
}

/**
 * World-space pointer projection. Mirrors ArrowDrawLayer2D's helper
 * (we duplicate locally rather than export-importing to avoid a
 * circular dependency and to keep the draw layer pure).
 *
 * Landscape mapping: SVG (x_svg, y_svg) → world (y_svg, -x_svg).
 */
function clientToWorld(
  svg: SVGSVGElement | null,
  clientX: number,
  clientY: number,
): { x: number; z: number } | null {
  if (!svg) return null;
  const ctm =
    typeof svg.getScreenCTM === 'function' ? svg.getScreenCTM() : null;
  if (!ctm) return null;
  const pt =
    typeof svg.createSVGPoint === 'function'
      ? svg.createSVGPoint()
      : new DOMPoint();
  pt.x = clientX;
  pt.y = clientY;
  const inverse = ctm.inverse();
  const transformed =
    'matrixTransform' in pt && typeof (pt as SVGPoint).matrixTransform === 'function'
      ? (pt as SVGPoint).matrixTransform(inverse)
      : (pt as DOMPoint).matrixTransform(inverse);
  return { x: transformed.y, z: -transformed.x };
}

/**
 * Resolve a "formation code or custom-formation id" emitted by the
 * FormationLibraryDrawer's onApply callback into a concrete spots[]
 * array for one team.
 *
 * Resolution order:
 *  1. seed FORMATIONS (handled by projectFormation) — works for the
 *     four canonical codes plus any future seed entry.
 *  2. EXTENDED_FORMATIONS — world-metres slots straight off the module.
 *  3. custom formations in localStorage — same shape as EXTENDED.
 *
 * Returns null when nothing matched so the caller can no-op cleanly.
 */
function resolveFormationToSpots(
  codeOrId: string,
  team: 'home' | 'away',
): { code: string; spots: PlayerSpot[] } | null {
  // Seed first — projectFormation falls back to DEFAULT_FORMATION on
  // unknown codes so we explicitly gate on SUPPORTED_FORMATIONS.
  if (SUPPORTED_FORMATIONS.includes(codeOrId)) {
    return { code: codeOrId, spots: projectFormation(codeOrId, team) };
  }
  // Extended library — slots are authored for home; mirror for away.
  const ext = EXTENDED_FORMATIONS[codeOrId];
  if (ext) {
    const slots = team === 'away' ? mirrorFormationForAway(ext.slots) : ext.slots;
    return {
      code: codeOrId,
      // y is unused at the layout level — 3D adds figure height.
      spots: slots.map((s) => ({ role: s.role, x: s.x, y: 0.6, z: s.z })),
    };
  }
  // Custom — look up by id.
  const customs = listCustomFormations();
  const cf = customs.find((c) => c.id === codeOrId);
  if (cf) {
    const slots = team === 'away' ? mirrorFormationForAway(cf.slots) : cf.slots;
    return {
      // The custom-id isn't a recognised "code" — keep the existing
      // home/away formation code on the layout (caller passes the
      // previous code), but we still emit spots.
      code: cf.id,
      spots: slots.map((s) => ({ role: s.role, x: s.x, y: 0.6, z: s.z })),
    };
  }
  return null;
}

export interface TacticsBoard2DProps {
  /** Initial home formation. */
  homeFormation?: string;
  /** Show the away team mirrored across the halfway line. */
  showAway?: boolean;
  /** Initial away formation. */
  awayFormation?: string;
  /**
   * Opaque key (typically `tactics-3d-${sessionId}`) under which the
   * board persists drag positions + sketched arrows to localStorage.
   * Shared with TacticsBoard3D — same key shows the same layout.
   * Omit to render an ephemeral board.
   */
  persistKey?: string;
  /** Optional display labels used by the SVG's aria-label builder. */
  homeLabel?: string;
  awayLabel?: string;
}

export function TacticsBoard2D({
  homeFormation = DEFAULT_FORMATION,
  showAway: showAwayInitial = true,
  awayFormation = DEFAULT_FORMATION,
  persistKey,
  homeLabel,
  awayLabel,
}: TacticsBoard2DProps) {
  // Coach can toggle opponent visibility via the View row chip.
  // Initial value comes from the prop so consumers can default to
  // home-only without forking the API.
  const [showAway, setShowAway] = useState<boolean>(showAwayInitial);
  // Set pieces moved to /setpieces as a sibling surface — Library menu
  // navigates there rather than opening an in-board drawer. The drawer
  // is still mounted for the "Create from current board" deep-link
  // (/setpieces → /2d?openSetPieces=1) so coaches can capture the live
  // shape into a new routine without leaving the page.
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Hydrate from localStorage if there's a saved layout for this key.
  // Same shape the 3D board reads, so layouts cross over both surfaces.
  const initial = useMemo(() => {
    if (!persistKey) return null;
    return loadTacticsLayout(persistKey);
  }, [persistKey]);

  const [home, setHome] = useState(initial?.homeFormation ?? homeFormation);
  const [away, setAway] = useState(initial?.awayFormation ?? awayFormation);
  const [view, setView] = useState<ViewPresetId>(DEFAULT_VIEW);
  const [homeSpots, setHomeSpots] = useState<PlayerSpot[]>(
    () => initial?.homeSpots ?? projectFormation(home, 'home'),
  );
  const [awaySpots, setAwaySpots] = useState<PlayerSpot[]>(
    () => initial?.awaySpots ?? projectFormation(away, 'away'),
  );
  const formationChangedRef = useRef(false);
  useEffect(() => {
    if (formationChangedRef.current) {
      setHomeSpots(projectFormation(home, 'home'));
    }
  }, [home]);
  useEffect(() => {
    if (formationChangedRef.current) {
      setAwaySpots(projectFormation(away, 'away'));
    }
  }, [away]);

  // Track explicit team-shape slider changes — initialized here so the
  // effect (declared below, after teamTactics is in scope) can read it.
  const teamShapeChangedRef = useRef(false);

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [mode, setMode] = useState<'move' | 'draw'>('move');
  const [arrows, setArrows] = useState<MovementArrow2DSpec[]>(
    () => initial?.arrows ?? [],
  );
  // Phase 3 — annotation slots. No interactive editing in this commit;
  // the surface just renders whatever the layout carries (v=1 entries
  // arrive with these fields undefined, hence the `?? []` fallback).
  const [zones, setZones] = useState<TacticsZone[]>(
    () => initial?.zones ?? [],
  );
  const [coachingPoints, setCoachingPoints] = useState<TacticsCoachingPoint[]>(
    () => initial?.coachingPoints ?? [],
  );

  // Phase 4 — drawer / picker open state. Each is board-local; the
  // trigger HudChip toggles it and the drawer's onClose closes it.
  const [patternsOpen, setPatternsOpen] = useState(false);
  // FormationLibraryDrawer state retained (its onApply path still
  // wires through handleApplyFormation for Brain / pattern-load
  // flows) but the chip that used to open it is gone — the
  // FormationDropdown on the Home/Away rows replaces it for the
  // standard codes. Custom + extended formations browsing can be
  // resurrected later via a "More…" footer in the dropdown.
  const [formationLibOpen, setFormationLibOpen] = useState(false);
  const [tacticsPanelOpen, setTacticsPanelOpen] = useState(false);
  const [tacticsPanelTeam, setTacticsPanelTeam] = useState<'home' | 'away'>('home');
  const [setPieceOpen, setSetPieceOpen] = useState(() =>
    searchParams.get('openSetPieces') === '1',
  );
  // Set-piece field-slice — when a set piece is applied the board crops
  // to just the relevant slice of pitch (a corner → the attacking
  // third) rather than the full 105×68 field. `setPieceRegion` holds
  // that slice; `pitchCropped` is the Slice⇄Full-pitch toggle. The crop
  // is purely a PitchSvg viewBox change — world coords stay untouched.
  const [setPieceRegion, setSetPieceRegion] = useState<FieldRegion | null>(null);
  const [pitchCropped, setPitchCropped] = useState(true);
  const [overlayPanelOpen, setOverlayPanelOpen] = useState(false);
  const [briefsOpen, setBriefsOpen] = useState(false);
  // Phase-mode editing — when 'phase' AND the walkthrough is snapped
  // onto a keyframe, drag commits go into that keyframe's spots in
  // place of the base homeSpots/awaySpots. Now auto-detected from
  // walkthrough state rather than an explicit chip toggle. The
  // editMode state machinery is dropped here; reintroduce if/when
  // we need an explicit "edit base while walkthrough is playing"
  // affordance.
  // Per-player tactical briefs (v=5 schema slot). Seeded from the
  // persisted layout on mount; coach taps "Briefs → Generate" to fill.
  const [playerBriefs, setPlayerBriefs] = useState<Record<string, PlayerBrief>>(
    () => (initial?.playerBriefs as Record<string, PlayerBrief> | undefined) ?? {},
  );
  const [playerBriefsStale, setPlayerBriefsStale] = useState<boolean>(
    () => initial?.playerBriefsStale ?? false,
  );
  const [playerBriefHistory, setPlayerBriefHistory] = useState<
    Record<string, PlayerBrief[]>
  >(
    () =>
      (initial?.playerBriefHistory as Record<string, PlayerBrief[]> | undefined) ??
      {},
  );
  // v=5 — coach-supplied roster identities (name + jersey number per
  // playerKey). Empty by default; coaches fill via the Briefs drawer.
  // Tokens + brief cards render initials / numbers from this when
  // present, fall back to the index otherwise.
  const [roster, setRoster] = useState<Record<string, { name?: string; number?: number }>>(
    () => (initial?.roster as Record<string, { name?: string; number?: number }> | undefined) ?? {},
  );
  // Role mode: when on, the next token tap opens the role picker for
  // that token instead of selecting it. Picker dismissal turns the
  // mode back off so coaches don't sit in a sticky state.
  const [roleMode, setRoleMode] = useState(false);
  const [roleTarget, setRoleTarget] = useState<
    { key: string; slot: string } | null
  >(null);
  // Drawing mode: separate from move/draw-arrow. While active a sibling
  // capture rect intercepts pointer events; arrow-draw remains intact
  // because the user can only be in one of mode='draw' (arrow), draw
  // mode (drawing), or neither.
  const [drawingMode, setDrawingMode] = useState(false);
  const [drawingKind, setDrawingKind] = useState<DrawingKind | null>(null);
  const [drawingStroke, setDrawingStroke] = useState<string>('#ffffff');
  const [drawingStrokeWidth, setDrawingStrokeWidth] = useState(0.35);
  const [drawingDash, setDrawingDash] = useState<DashStyle>('solid');
  const [drawingArrowHead, setDrawingArrowHead] = useState<ArrowHead>('standard');

  // Phase 4 — state slots. Lazy-init for team tactics so a v=1 layout
  // doesn't allocate a full bundle until the coach actually opens the
  // panel; the rest seed cheaply from the loaded layout.
  const [playerRoles, setPlayerRoles] = useState<
    Record<string, PlayerRoleSelection>
  >(
    () =>
      (initial?.playerRoles as Record<string, PlayerRoleSelection> | undefined) ??
      {},
  );
  const [teamTactics, setTeamTactics] = useState<TeamTacticsBundle | undefined>(
    () => initial?.teamTactics as TeamTacticsBundle | undefined,
  );

  // Spatial sliders → reshape the pitch. When the coach drags
  // lineHeight / width / depth via the TeamTacticsPanel, the team's
  // shape on the pitch should shift to match. Tracked by
  // teamShapeChangedRef (set when the coach edits a slider; cleared
  // on initial mount + on external rehydrates) so the loaded layout
  // isn't blown away on first paint.
  const homeSpatialKey = useMemo(() => {
    const side = teamTactics?.home;
    if (!side) return null;
    const active = side.slots.find((s) => s.id === side.activeSlotId);
    if (!active) return null;
    const s = active.tactics.sliders;
    return `${s.lineHeight ?? 50}|${s.width ?? 50}|${s.depth ?? 50}`;
  }, [teamTactics]);
  useEffect(() => {
    if (!teamShapeChangedRef.current) return;
    teamShapeChangedRef.current = false;
    const side = teamTactics?.home;
    if (!side) return;
    const active = side.slots.find((s) => s.id === side.activeSlotId);
    if (!active) return;
    setHomeSpots(
      applyTeamShapeTransform(
        projectFormation(home, 'home'),
        active.tactics,
        'home',
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homeSpatialKey]);

  const [setPieces, setSetPieces] = useState<TacticsSetPiece[]>(
    () => (initial?.setPieces as TacticsSetPiece[] | undefined) ?? [],
  );
  const [drawings, setDrawings] = useState<TacticsDrawing[]>(
    () => (initial?.drawings as TacticsDrawing[] | undefined) ?? [],
  );
  const [overlays, setOverlays] = useState<TacticsOverlaysState>(
    () =>
      (initial?.overlays as TacticsOverlaysState | undefined) ??
      defaultOverlaysState(),
  );
  // Spacing focus mode — drives BOTH the points fed into voronoi
  // compute AND the cells shown at render. 'both' uses every player
  // (current behaviour); 'home' / 'away' recomputes as if only that
  // team exists, so the chosen team's cells expand across the whole
  // pitch unbounded. Board-local; not persisted yet.
  const [voronoiFocus, setVoronoiFocus] = useState<'both' | 'home' | 'away'>('both');
  // Drag-then-pick: set when a drag commits to a different zone.
  // The picker UI then asks the coach to confirm or change the slot.
  const [slotPicker, setSlotPicker] = useState<
    { key: string; currentSlot: string; suggested: string } | null
  >(null);

  // Phase 2/3 — keyframes are local state so Phase-mode edits can
  // mutate the active keyframe in place. Declared early so the save
  // effect's dependency array can reference it without a TDZ error.
  const [layoutKeyframes, setLayoutKeyframes] = useState(
    () => initial?.keyframes ?? [],
  );

  // External writers (Brain commits, future multi-tab sync) update
  // localStorage via saveTacticsLayout; this listener rehydrates the
  // board's local state. selfSaveRef short-circuits our OWN auto-save
  // emits so the listener doesn't react to changes it caused itself —
  // mirrors the same guard on the 3D board.
  const selfSaveRef = useRef(false);
  useEffect(() => {
    if (!persistKey) return;
    return onTacticsLayoutChanged(persistKey, () => {
      if (selfSaveRef.current) {
        selfSaveRef.current = false;
        return;
      }
      const fresh = loadTacticsLayout(persistKey);
      if (!fresh) return;
      setHome(fresh.homeFormation);
      setAway(fresh.awayFormation);
      setHomeSpots(fresh.homeSpots);
      setAwaySpots(fresh.awaySpots);
      setArrows(fresh.arrows);
      // Phase 3 fields may be undefined on a v=1/v=2 entry that just
      // landed via an external writer — coalesce to empty arrays so the
      // surface always has something to render.
      setZones(fresh.zones ?? []);
      setCoachingPoints(fresh.coachingPoints ?? []);
      // Phase 4 slots — every field is optional in TacticsLayout so we
      // coalesce to a sensible empty value rather than blanking state
      // every time an external writer touches an older layout.
      setPlayerRoles(
        (fresh.playerRoles as Record<string, PlayerRoleSelection> | undefined) ??
          {},
      );
      setTeamTactics(fresh.teamTactics as TeamTacticsBundle | undefined);
      setSetPieces((fresh.setPieces as TacticsSetPiece[] | undefined) ?? []);
      setDrawings((fresh.drawings as TacticsDrawing[] | undefined) ?? []);
      setOverlays(
        (fresh.overlays as TacticsOverlaysState | undefined) ??
          defaultOverlaysState(),
      );
      // v=5 — briefs + stale flag + history follow the same external-
      // writer pattern. Brain commits a famous tactic or another tab
      // regenerates briefs → we pick those up alongside the rest.
      setPlayerBriefs(
        (fresh.playerBriefs as Record<string, PlayerBrief> | undefined) ?? {},
      );
      setPlayerBriefsStale(fresh.playerBriefsStale ?? false);
      setPlayerBriefHistory(
        (fresh.playerBriefHistory as Record<string, PlayerBrief[]> | undefined) ?? {},
      );
      // Phase mode — keyframes are local state now, so external writers
      // (Brain Bar sequence commits, multi-tab edits) need to flow back
      // into the walkthrough.
      setLayoutKeyframes(fresh.keyframes ?? []);
      setRoster(
        (fresh.roster as Record<string, { name?: string; number?: number }> | undefined) ?? {},
      );
    });
  }, [persistKey]);
  const [arrowColor, setArrowColor] = useState<'home' | 'away' | 'neutral'>('home');
  const arrowColorHex =
    arrowColor === 'home' ? '#4ade80' : arrowColor === 'away' ? '#fbbf24' : '#f5f5f5';
  const [arrowSeed, setArrowSeed] = useState<
    { origin: { x: number; z: number }; playerKey: string | null } | null
  >(null);
  const [savedAt, setSavedAt] = useState<string | null>(initial?.saved_at ?? null);

  // ---- Player-brief handlers (declared after every input state above so
  //      the useCallback deps don't reference vars still in TDZ). ----
  const [briefsBusy, setBriefsBusy] = useState(false);
  const handleRegenerateBriefs = useCallback(
    async (options: { polish: boolean } = { polish: false }) => {
      const layout: TacticsLayout = {
        v: 5,
        homeFormation: home,
        awayFormation: away,
        homeSpots,
        awaySpots,
        arrows,
        zones,
        coachingPoints,
        playerRoles,
        teamTactics: teamTactics as TacticsLayout['teamTactics'],
        setPieces: setPieces as unknown as TacticsLayout['setPieces'],
        drawings: drawings as unknown as TacticsLayout['drawings'],
        saved_at: savedAt ?? new Date().toISOString(),
      };
      const rules = generateAllPlayerBriefs(layout, 'home');
      // Preserve coach's manual edits across regens — they take
      // precedence over the rule output. Restore manual briefs from
      // the prior state; only the non-manual ones get replaced.
      const writeMap = (nextRules: PlayerBrief[]): Record<string, PlayerBrief> => {
        const out: Record<string, PlayerBrief> = {};
        for (const b of nextRules) {
          const prev = playerBriefs[b.playerKey];
          out[b.playerKey] = prev?.source === 'manual' ? prev : b;
        }
        return out;
      };
      // Push each pre-regen brief onto history so the coach can roll
      // back. Only push briefs that already exist — first-time regen
      // has nothing to remember.
      let nextHistory = playerBriefHistory;
      for (const k of Object.keys(playerBriefs)) {
        const prior = playerBriefs[k];
        if (prior) nextHistory = appendBriefHistory(nextHistory, k, prior);
      }
      setPlayerBriefHistory(nextHistory);
      setPlayerBriefs(writeMap(rules));
      setPlayerBriefsStale(false);

      if (!options.polish) return;
      setBriefsBusy(true);
      try {
        const polished = await polishAllPlayerBriefs(rules, layout, 'home');
        setPlayerBriefs(writeMap(polished));
      } catch {
        // Polish is strictly additive — on any failure the rule output
        // we wrote above stays put. No user-facing error needed.
      } finally {
        setBriefsBusy(false);
      }
    },
    [
      home,
      away,
      homeSpots,
      awaySpots,
      arrows,
      zones,
      coachingPoints,
      playerRoles,
      teamTactics,
      setPieces,
      drawings,
      savedAt,
      playerBriefs,
      playerBriefHistory,
    ],
  );

  const handleEditBrief = useCallback(
    (playerKey: string, next: PlayerBrief) => {
      setPlayerBriefs((prev) => {
        const prior = prev[playerKey];
        if (prior) {
          setPlayerBriefHistory((h) => appendBriefHistory(h, playerKey, prior));
        }
        return { ...prev, [playerKey]: next };
      });
    },
    [],
  );

  const handleRestoreBrief = useCallback(
    (playerKey: string, version: PlayerBrief) => {
      setPlayerBriefs((prev) => {
        const prior = prev[playerKey];
        if (prior) {
          setPlayerBriefHistory((h) => appendBriefHistory(h, playerKey, prior));
        }
        return { ...prev, [playerKey]: version };
      });
    },
    [],
  );

  // Stale-tracking effect — when brief-affecting inputs change AND we
  // already have at least one brief, flip stale=true. Skips the first
  // mount (briefsInputsRef seeded from initial render).
  const briefsInputsKey = useMemo(
    () =>
      JSON.stringify({
        home,
        away,
        homeSpots,
        awaySpots,
        arrows,
        teamTactics,
        playerRoles,
        setPieces,
      }),
    [home, away, homeSpots, awaySpots, arrows, teamTactics, playerRoles, setPieces],
  );
  const briefsInputsRef = useRef(briefsInputsKey);
  useEffect(() => {
    if (briefsInputsKey === briefsInputsRef.current) return;
    briefsInputsRef.current = briefsInputsKey;
    if (Object.keys(playerBriefs).length > 0) setPlayerBriefsStale(true);
  }, [briefsInputsKey, playerBriefs]);

  const PLAYBACK_DURATION_MS = 3200;
  const [playbackProgress, setPlaybackProgress] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  // Per-arrow piecewise sample lookup. Recomputed only when arrows
  // change so RAF playback doesn't allocate.
  const playbackPositions = useMemo(() => {
    const out = new Map<string, { x: number; z: number }>();
    if (!isPlaying && playbackProgress === 0) return out;
    for (const a of arrows) {
      if (!a.playerKey || a.points.length < 2) continue;
      const t = Math.min(Math.max(playbackProgress, 0), 1);
      out.set(a.playerKey, linearArcLengthSample(a.points, t));
    }
    return out;
  }, [arrows, playbackProgress, isPlaying]);

  // RAF loop while playing — mirrors the 3D board's playback effect.
  // Reduced motion: snap to t=1 on play, suppress the animation loop.
  const reducedMotion = useReducedMotion();
  useEffect(() => {
    if (!isPlaying) return;
    if (reducedMotion) {
      setPlaybackProgress(1);
      setIsPlaying(false);
      return;
    }
    let raf = 0;
    const startTs = performance.now() - playbackProgress * PLAYBACK_DURATION_MS;
    const tick = () => {
      const elapsed = performance.now() - startTs;
      const next = Math.min(elapsed / PLAYBACK_DURATION_MS, 1);
      setPlaybackProgress(next);
      if (next >= 1) {
        setIsPlaying(false);
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // intentionally omit playbackProgress — reading it on each tick start so
    // a Stop+Play resets to 0 cleanly without re-baking the interval.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, reducedMotion]);

  const boundArrowCount = useMemo(
    () => arrows.filter((a) => a.playerKey).length,
    [arrows],
  );

  // Debounced persistence — same 400ms window as the 3D board.
  useEffect(() => {
    if (!persistKey) return;
    const t = setTimeout(() => {
      selfSaveRef.current = true;
      const entry = saveTacticsLayout(persistKey, {
        homeFormation: home,
        awayFormation: away,
        homeSpots,
        awaySpots,
        arrows,
        // Phase 3 — preserve annotations on every write so the surface
        // round-trips zones + coaching points alongside spots/arrows.
        zones,
        coachingPoints,
        // Phase 4 — every new slot rides on the same auto-save. Keep
        // teamTactics undefined when the coach hasn't touched it so a
        // v=1 layout stays compact in storage. We cast at the boundary
        // because the layout schema uses permissive
        // `Record<string, unknown>` aliases (see ./types.ts) while we
        // hold the strictly-typed shapes from the feature modules in
        // local state — the runtime data is identical, just the index
        // signatures differ.
        playerRoles,
        teamTactics: teamTactics as TacticsLayout['teamTactics'],
        setPieces: setPieces as unknown as TacticsLayout['setPieces'],
        drawings: drawings as unknown as TacticsLayout['drawings'],
        overlays: overlays as unknown as TacticsLayout['overlays'],
        playerBriefs: playerBriefs as unknown as TacticsLayout['playerBriefs'],
        playerBriefsStale,
        playerBriefHistory: playerBriefHistory as unknown as TacticsLayout['playerBriefHistory'],
        keyframes: layoutKeyframes,
        roster,
      });
      setSavedAt(entry.saved_at);
    }, 400);
    return () => clearTimeout(t);
  }, [
    persistKey,
    home,
    away,
    homeSpots,
    awaySpots,
    arrows,
    zones,
    coachingPoints,
    playerRoles,
    teamTactics,
    setPieces,
    drawings,
    overlays,
    playerBriefs,
    playerBriefsStale,
    playerBriefHistory,
    layoutKeyframes,
    roster,
  ]);

  // Phase 4 — undo/redo history. Listens to the same change events as
  // the persistence path so every commit is a candidate snapshot.
  const history = useTacticsHistory(persistKey);

  // layoutKeyframes is declared earlier (near the top of state) so the
  // save effect can reference it. Branches + voiceovers remain
  // read-only memo derivatives — no editor yet.
  const layoutBranches = useMemo(() => initial?.branches ?? [], [initial]);
  const layoutVoiceovers = useMemo(() => initial?.voiceovers ?? [], [initial]);
  const walkthrough = useWalkthroughPlayback({
    keyframes: layoutKeyframes,
    branches: layoutBranches,
    voiceovers: layoutVoiceovers,
    reducedMotion,
  });
  const [walkthroughTtsEnabled, setWalkthroughTtsEnabled] = useState(true);
  const hasKeyframes = layoutKeyframes.length >= 2;

  // Active keyframe-being-edited — auto-detected from the walkthrough.
  // When the walkthrough HUD is showing a non-base view (progress > 0
  // or playing) AND parked on a specific keyframe, drags target that
  // keyframe's spots instead of the base homeSpots. Without this,
  // tokenSpotFor (which reads from walkthrough.positions during
  // playback) would return the sampled keyframe position while the
  // drag committed to a hidden homeSpots — token snaps back, drag
  // appears broken. The editMode chip is hidden in this build but the
  // state is kept for future explicit override surfaces.
  const walkthroughActiveNow =
    hasKeyframes && (walkthrough.isPlaying || walkthrough.progress > 0);
  const activeEditKeyframeId = walkthroughActiveNow
    ? walkthrough.activeKeyframeId
    : null;

  // Drag commit:
  //   - Updates position via clampToPitch.
  //   - Does NOT auto-rewrite role — that's the coach's call.
  //   - If the inferred role for the new position differs from the
  //     current role (i.e. the drag crossed a zone boundary),
  //     `setSlotPicker` opens an explicit picker. Small drags that
  //     stay in-zone fall through silently.
  const moveHomeSpot = useCallback(
    (i: number) => (next: { x: number; z: number }) => {
      // Phase mode + parked on a keyframe → write into that keyframe.
      if (activeEditKeyframeId) {
        setLayoutKeyframes((prev) =>
          prev.map((k) => {
            if (k.id !== activeEditKeyframeId) return k;
            const homeSpots = k.homeSpots.map((s, idx) => {
              if (idx !== i) return s;
              // Drag → position only. Auto-picker on zone crossing
              // disabled per coach feedback (intrusive on free drags).
              return clampToPitch(s, next.x, next.z);
            });
            return { ...k, homeSpots };
          }),
        );
        return;
      }
      setHomeSpots((prev) =>
        prev.map((s, idx) => {
          if (idx !== i) return s;
          const clamped = clampToPitch(s, next.x, next.z);
          // Zone-crossing drag → open the slot picker so the coach
          // can confirm or change the role. Position is already
          // committed; picker only governs the role label.
          // Suppressed in set-piece mode: a corner deliberately sends
          // players miles from their formation slot, so the role-swap
          // prompt would fire on every drag.
          const inferred = inferRoleAtPosition(clamped.x, clamped.z, 'home');
          if (inferred !== s.role && !setPieceRegion) {
            setSlotPicker({ key: `home-${i}`, currentSlot: s.role, suggested: inferred });
          }
          return clamped;
        }),
      );
    },
    [activeEditKeyframeId, setPieceRegion],
  );
  const moveAwaySpot = useCallback(
    (i: number) => (next: { x: number; z: number }) => {
      if (activeEditKeyframeId) {
        setLayoutKeyframes((prev) =>
          prev.map((k) => {
            if (k.id !== activeEditKeyframeId) return k;
            const awaySpots = k.awaySpots.map((s, idx) => {
              if (idx !== i) return s;
              return clampToPitch(s, next.x, next.z);
            });
            return { ...k, awaySpots };
          }),
        );
        return;
      }
      setAwaySpots((prev) =>
        prev.map((s, idx) => {
          if (idx !== i) return s;
          const clamped = clampToPitch(s, next.x, next.z);
          // See moveHomeSpot — slot picker is off in set-piece mode.
          const inferred = inferRoleAtPosition(clamped.x, clamped.z, 'away');
          if (inferred !== s.role && !setPieceRegion) {
            setSlotPicker({ key: `away-${i}`, currentSlot: s.role, suggested: inferred });
          }
          return clamped;
        }),
      );
    },
    [activeEditKeyframeId, setPieceRegion],
  );

  const svgRef = useRef<SVGSVGElement | null>(null);

  const clearSelection = useCallback(() => setSelectedKey(null), []);

  // Arrow keys nudge the selected token, Escape clears selection.
  const NUDGE_DISTANCE = 1;
  useEffect(() => {
    if (!selectedKey) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setSelectedKey(null);
        return;
      }
      const dx = e.key === 'ArrowLeft' ? -NUDGE_DISTANCE : e.key === 'ArrowRight' ? NUDGE_DISTANCE : 0;
      const dz = e.key === 'ArrowUp' ? -NUDGE_DISTANCE : e.key === 'ArrowDown' ? NUDGE_DISTANCE : 0;
      if (dx === 0 && dz === 0) return;
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return;
      }
      e.preventDefault();
      const [side, idxStr] = selectedKey.split('-');
      const idx = Number(idxStr);
      if (side === 'home') {
        setHomeSpots((prev) =>
          prev.map((s, i) => (i === idx ? clampToPitch(s, s.x + dx, s.z + dz) : s)),
        );
      } else if (side === 'away') {
        setAwaySpots((prev) =>
          prev.map((s, i) => (i === idx ? clampToPitch(s, s.x + dx, s.z + dz) : s)),
        );
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedKey]);

  // Cmd/Ctrl+Z = undo, Cmd/Ctrl+Shift+Z = redo. Wired at the document
  // level so the shortcuts work without focusing the HUD.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable)
      ) {
        return;
      }
      const mod = e.ctrlKey || e.metaKey;
      if (!mod) return;
      const key = e.key.toLowerCase();
      if (key === 'z' && !e.shiftKey) {
        e.preventDefault();
        history.undo();
      } else if ((key === 'z' && e.shiftKey) || key === 'y') {
        e.preventDefault();
        history.redo();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [history]);

  // Polite live-region announcement when selection changes. Building
  // the string from spot data here (instead of from the token's
  // aria-label) keeps the wording stable if the token's label evolves.
  const selectionAnnouncement = useMemo(() => {
    if (!selectedKey) return 'Selection cleared';
    const [side, idxStr] = selectedKey.split('-');
    const idx = Number(idxStr);
    const spot = side === 'home' ? homeSpots[idx] : awaySpots[idx];
    if (!spot) return '';
    const teamLabel = side === 'home' ? 'Home' : 'Away';
    return `${teamLabel} #${idx + 1} — ${spot.role} selected; arrow keys to nudge by one metre`;
  }, [selectedKey, homeSpots, awaySpots]);

  const playbackPct = Math.round(playbackProgress * 100);

  // Pointer-down on the SVG background clears selection (when not in
  // draw mode — there the rect captures the event for arrow sketching).
  const onSvgPointerDown = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      if (mode === 'draw') return;
      if (drawingMode) return;
      // If the event came from a child (token), let the child handle it.
      if (e.target !== e.currentTarget) return;
      clearSelection();
    },
    [clearSelection, mode, drawingMode],
  );

  // Phase 4 — pattern recall. The PatternDrawer fires this with a
  // pattern id; we resolve the full layout via the store and write it
  // through the canonical save path. The existing
  // `onTacticsLayoutChanged` listener above sees the emit and
  // re-hydrates board state, so we don't need to touch local setters
  // here.
  //
  // INTENTIONAL: this clobbers the active session's saved layout. The
  // debounced auto-save (400ms) will rewrite the now-loaded pattern as
  // the board's new state on the next mutation. That is the desired
  // behaviour — "pulling up Tuesday press" replaces what's on the pitch
  // by design. If a coach wants to keep the previous layout they can
  // save it as its own pattern first.
  const handleSelectPattern = useCallback(
    (id: string) => {
      if (!persistKey) {
        setPatternsOpen(false);
        return;
      }
      const pat = getPattern(id);
      if (!pat) {
        setPatternsOpen(false);
        return;
      }
      const { v: _v, saved_at: _savedAt, ...layout } = pat.layout;
      saveTacticsLayout(persistKey, layout);
      setPatternsOpen(false);
    },
    [persistKey],
  );

  // Apply a formation from the library drawer to the named team. We
  // route via setHome/setAway so the existing formation-change effect
  // re-projects the spots — preserving the seed flow — and just
  // overlay extended/custom spots directly when there's no projection
  // path available.
  const handleApplyFormation = useCallback(
    (codeOrId: string, team: 'home' | 'away') => {
      const resolved = resolveFormationToSpots(codeOrId, team);
      if (!resolved) {
        setFormationLibOpen(false);
        return;
      }
      formationChangedRef.current = true;
      if (team === 'home') {
        // Seed formations re-project via the existing effect when we
        // set home — so we just set the code and let it run. Extended +
        // custom shapes don't live in projectFormation's lookup, so we
        // explicitly write spots and only update the code label.
        if (SUPPORTED_FORMATIONS.includes(codeOrId)) {
          setHome(codeOrId);
        } else {
          setHome(resolved.code);
          setHomeSpots(resolved.spots);
        }
      } else {
        if (SUPPORTED_FORMATIONS.includes(codeOrId)) {
          setAway(codeOrId);
        } else {
          setAway(resolved.code);
          setAwaySpots(resolved.spots);
        }
      }
      setFormationLibOpen(false);
    },
    [],
  );

  // Set piece: apply a saved play. Set-piece spots are pure top-down
  // (no y); we re-stamp y=0.6 so the spot shape matches the 3D board's
  // figure-height expectation.
  const handleApplySetPiece = useCallback((piece: TacticsSetPiece) => {
    const stamp = (s: { role: string; x: number; z: number }): PlayerSpot => ({
      role: s.role,
      x: s.x,
      y: 0.6,
      z: s.z,
    });
    setHomeSpots(piece.homeSpots.map(stamp));
    setAwaySpots(piece.awaySpots.map(stamp));
    if (piece.arrows && Array.isArray(piece.arrows)) {
      // Replace existing arrows on apply — same "clobber" pattern as
      // pattern recall. The piece's arrows include their own colours +
      // bindings; trust the snapshot.
      setArrows(
        piece.arrows.map((a) => ({
          id: a.id,
          points: a.points,
          color: a.color,
          playerKey: a.playerKey,
        })),
      );
    }
    // Crop the pitch to this set piece's slice (corner → attacking
    // third, throw-in → touchline band, etc.). The Slice⇄Full-pitch
    // chip lets the coach zoom back out.
    setSetPieceRegion(fieldRegionForSetPiece(piece.type, piece.origin));
    setPitchCropped(true);
    // Reset the walkthrough — while it's playing/scrubbed, tokenSpotFor
    // renders keyframe-sampled positions, which would mask the spots we
    // just applied (the coach would see "nothing changed"). Reset drops
    // progress to 0 so the fresh set-piece spots are what's drawn.
    walkthrough.reset();
    setSetPieceOpen(false);
  }, [walkthrough]);

  // ── /setpieces deep-link: open a saved routine ────────────────────
  // The /setpieces page "Open" action navigates here as
  // `/2d?setpiece=<id>`. On mount we resolve that id via the shared
  // store and snap the board to the piece. Guarded by a ref so it
  // fires exactly once even if the effect body re-runs (StrictMode
  // double-invoke, or a searchParams identity change) — we never want
  // a saved routine to clobber the board twice or after the coach has
  // started editing.
  const setPieceDeepLinkRef = useRef(false);
  useEffect(() => {
    if (setPieceDeepLinkRef.current) return;
    setPieceDeepLinkRef.current = true;
    const id = searchParams.get('setpiece');
    if (!id) return;
    const piece = getSetPiece(id);
    if (piece) handleApplySetPiece(piece);
  }, [searchParams, handleApplySetPiece]);

  // ── /setpieces deep-link: pre-select the drawer tab ───────────────
  // The /setpieces "+ Create" action navigates here as
  // `/2d?openSetPieces=1&type=<SetPieceType>`. `openSetPieces` already
  // seeds `setPieceOpen`; here we read `type` and — when it is one of
  // the eight valid SetPieceType values — pass it to the drawer as
  // `initialType` so the matching tab is active on open. An absent or
  // unrecognised value resolves to `undefined` (drawer falls back to
  // its own default tab).
  const initialSetPieceType = useMemo<SetPieceType | undefined>(() => {
    const raw = searchParams.get('type');
    return raw && (SET_PIECE_TYPES as readonly string[]).includes(raw)
      ? (raw as SetPieceType)
      : undefined;
  }, [searchParams]);

  // ── Set-piece create mode: lay out the default shape + zoom ───────
  // Opening the board to *build* a corner (`?type=corner-att`) lays out
  // that type's default shape — players in the box, ball at the flag —
  // and zooms onto it via handleApplySetPiece. Without this "+ Create"
  // just zoomed the running 4-3-3, which read as a broken half-pitch
  // view with no corner in it. Fires once; the coach edits from there.
  const setPieceTypeCropRef = useRef(false);
  useEffect(() => {
    if (setPieceTypeCropRef.current) return;
    setPieceTypeCropRef.current = true;
    if (!initialSetPieceType) return;
    // Attacking corners use the conventional-order starter template so
    // players keep their numbers (no position swap); other types fall
    // back to the generic default layout.
    const layout =
      initialSetPieceType === 'corner-att'
        ? CORNER_TEMPLATES[0]
        : defaultSetPiece(initialSetPieceType);
    handleApplySetPiece({
      ...layout,
      id: `draft-${initialSetPieceType}`,
      createdAt: new Date().toISOString(),
    });
    // handleApplySetPiece closes the drawer (right for *applying* a
    // saved routine) — but create mode wants it open so the coach can
    // tweak the shape and save via "New from current board".
    setSetPieceOpen(true);
  }, [initialSetPieceType, handleApplySetPiece]);

  // Set piece: save the current board as a new set piece. We snapshot
  // home/away spots + the active arrows; the type comes from the
  // drawer's current tab.
  const handleSaveSetPiece = useCallback(
    ({ name, type }: { name: string; type: TacticsSetPiece['type'] }) => {
      const stripY = (s: PlayerSpot) => ({ role: s.role, x: s.x, z: s.z });
      const piece = saveSetPiece({
        name,
        type,
        // We don't have an "origin" picker yet; use pitch centre as a
        // pragmatic placeholder. Coach can edit later.
        origin: { x: 0, z: 0 },
        homeSpots: homeSpots.map(stripY),
        awaySpots: awaySpots.map(stripY),
        arrows: arrows.map((a) => ({
          id: a.id,
          points: a.points,
          color: a.color,
          playerKey: a.playerKey,
        })),
      });
      setSetPieces((prev) => [...prev, piece]);
    },
    [arrows, awaySpots, homeSpots],
  );

  // Token selection handler — branches on role mode. In role mode the
  // next tap on a token opens the role picker rather than selecting.
  const handleTokenSelect = useCallback(
    (key: string) => {
      if (roleMode) {
        const [side, idxStr] = key.split('-');
        const idx = Number(idxStr);
        const spot = side === 'home' ? homeSpots[idx] : awaySpots[idx];
        if (!spot) return;
        setRoleTarget({ key, slot: spot.role });
        // Don't exit role mode here — the picker's onClose toggles it.
        return;
      }
      setSelectedKey(key);
    },
    [roleMode, homeSpots, awaySpots],
  );

  // Drawing capture — sibling overlay rect, active only when
  // drawingMode + a kind are set. Same projection math as
  // ArrowDrawLayer2D; we accumulate world-metres samples and commit on
  // pointer-up.
  const drawingPtsRef = useRef<Array<{ x: number; z: number }>>([]);
  const drawingActiveRef = useRef(false);
  const [drawingPreview, setDrawingPreview] = useState<TacticsDrawing | null>(
    null,
  );
  const MIN_DRAW_SEGMENT = 0.6;

  const finishDrawing = useCallback(
    (final: Array<{ x: number; z: number }>) => {
      if (!drawingKind) return;
      // line / arrow / curved-arrow / rect / ellipse use start+end; the
      // freehand kind keeps the full sample list.
      const id = `drawing-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
      const defaults = defaultDrawing(drawingKind);
      const points =
        drawingKind === 'freehand'
          ? final
          : final.length >= 2
            ? [final[0], final[final.length - 1]]
            : final;
      if (drawingKind !== 'text' && points.length < 2) return;
      const next: TacticsDrawing = {
        ...defaults,
        id,
        kind: drawingKind,
        points: drawingKind === 'text' ? [final[0]] : points,
        stroke: drawingStroke,
        strokeWidth: drawingStrokeWidth,
        dash: drawingDash,
      };
      if (drawingKind === 'arrow' || drawingKind === 'curved-arrow') {
        next.arrowHead = drawingArrowHead;
      }
      setDrawings((prev) => [...prev, next]);
    },
    [drawingArrowHead, drawingDash, drawingKind, drawingStroke, drawingStrokeWidth],
  );

  const onDrawingPointerDown = useCallback(
    (e: React.PointerEvent<SVGRectElement>) => {
      if (!drawingMode || !drawingKind) return;
      e.stopPropagation();
      const pt = clientToWorld(svgRef.current, e.clientX, e.clientY);
      if (!pt) return;
      drawingActiveRef.current = true;
      drawingPtsRef.current = [pt];
      (e.target as Element).setPointerCapture?.(e.pointerId);
      const defaults = defaultDrawing(drawingKind);
      setDrawingPreview({
        ...defaults,
        id: 'preview',
        kind: drawingKind,
        points: [pt],
        stroke: drawingStroke,
        strokeWidth: drawingStrokeWidth,
        dash: drawingDash,
        ...(drawingKind === 'arrow' || drawingKind === 'curved-arrow'
          ? { arrowHead: drawingArrowHead }
          : {}),
      });
    },
    [
      drawingMode,
      drawingKind,
      drawingStroke,
      drawingStrokeWidth,
      drawingDash,
      drawingArrowHead,
    ],
  );

  const onDrawingPointerMove = useCallback(
    (e: React.PointerEvent<SVGRectElement>) => {
      if (!drawingActiveRef.current || !drawingKind) return;
      e.stopPropagation();
      const pt = clientToWorld(svgRef.current, e.clientX, e.clientY);
      if (!pt) return;
      const last = drawingPtsRef.current[drawingPtsRef.current.length - 1];
      const d = Math.hypot(pt.x - last.x, pt.z - last.z);
      if (d < MIN_DRAW_SEGMENT) return;
      drawingPtsRef.current = [...drawingPtsRef.current, pt];
      const defaults = defaultDrawing(drawingKind);
      // For non-freehand shapes the preview shows start + current.
      const previewPts =
        drawingKind === 'freehand'
          ? drawingPtsRef.current
          : [drawingPtsRef.current[0], pt];
      setDrawingPreview({
        ...defaults,
        id: 'preview',
        kind: drawingKind,
        points: previewPts,
        stroke: drawingStroke,
        strokeWidth: drawingStrokeWidth,
        dash: drawingDash,
        ...(drawingKind === 'arrow' || drawingKind === 'curved-arrow'
          ? { arrowHead: drawingArrowHead }
          : {}),
      });
    },
    [
      drawingKind,
      drawingStroke,
      drawingStrokeWidth,
      drawingDash,
      drawingArrowHead,
    ],
  );

  const onDrawingPointerUp = useCallback(
    (e: React.PointerEvent<SVGRectElement>) => {
      if (!drawingActiveRef.current) return;
      e.stopPropagation();
      drawingActiveRef.current = false;
      (e.target as Element).releasePointerCapture?.(e.pointerId);
      const final = drawingPtsRef.current;
      drawingPtsRef.current = [];
      setDrawingPreview(null);
      if (final.length >= 1) finishDrawing(final);
    },
    [finishDrawing],
  );

  // ── Polygon-zone capture ──────────────────────────────────────────
  // The zone tool is corner-by-corner, not drag-based: each tap adds a
  // vertex; a double-tap closes the polygon. We keep separate refs so
  // the polygon's in-progress state survives between taps, and route
  // pointer events to these handlers when drawingKind === 'zone'.
  const polygonPtsRef = useRef<Array<{ x: number; z: number }>>([]);
  const lastTapRef = useRef<{ ts: number; x: number; z: number } | null>(null);
  const zoneDownPosRef = useRef<{ x: number; z: number } | null>(null);
  // Cursor position over the capture surface — drives the dashed
  // guide line in <ZonePolygonOverlay>. State (not ref) because the
  // overlay re-renders on every move; null when the pointer isn't on
  // the capture rect or zone tool isn't active.
  const [zoneHover, setZoneHover] = useState<{ x: number; z: number } | null>(
    null,
  );
  // Mirror of polygonPtsRef in state so the overlay re-renders when a
  // corner is committed. We keep both: the ref drives the
  // tap/double-tap detector (synchronous reads), the state drives the
  // overlay.
  const [polygonCorners, setPolygonCorners] = useState<
    Array<{ x: number; z: number }>
  >([]);
  // Treat pointer-up within 1.5m of pointer-down as a tap (anything
  // larger was a drag the coach probably aborted). Double-tap window
  // matches OS-level conventions: 500ms / ~2m co-location.
  const TAP_TOLERANCE_M = 1.5;
  const DOUBLE_TAP_MS = 500;
  const DOUBLE_TAP_TOLERANCE_M = 2.0;

  const buildZonePreview = useCallback(
    (corners: Array<{ x: number; z: number }>): TacticsDrawing | null => {
      if (corners.length === 0) return null;
      const defaults = defaultDrawing('zone');
      return {
        ...defaults,
        id: 'preview',
        kind: 'zone',
        points: corners,
        stroke: drawingStroke,
        strokeWidth: drawingStrokeWidth,
        dash: drawingDash,
      };
    },
    [drawingStroke, drawingStrokeWidth, drawingDash],
  );

  const finishZonePolygon = useCallback(
    (corners: Array<{ x: number; z: number }>) => {
      // ≥3 corners required for a polygon; anything shorter we discard
      // so an accidental double-tap on the first vertex doesn't create
      // a degenerate shape.
      if (corners.length < 3) return;
      const defaults = defaultDrawing('zone');
      const id = `drawing-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
      const next: TacticsDrawing = {
        ...defaults,
        id,
        kind: 'zone',
        points: corners,
        stroke: drawingStroke,
        strokeWidth: drawingStrokeWidth,
        dash: drawingDash,
      };
      setDrawings((prev) => [...prev, next]);
    },
    [drawingStroke, drawingStrokeWidth, drawingDash],
  );

  const onZonePointerDown = useCallback(
    (e: React.PointerEvent<SVGRectElement>) => {
      if (!drawingMode || drawingKind !== 'zone') return;
      e.stopPropagation();
      const pt = clientToWorld(svgRef.current, e.clientX, e.clientY);
      if (!pt) return;
      // Record where the press started — used at pointer-up to decide
      // tap-vs-drag. We don't capture the pointer here because the
      // zone tool consumes a sequence of taps, not one continuous drag.
      zoneDownPosRef.current = pt;
    },
    [drawingMode, drawingKind],
  );

  const onZonePointerMove = useCallback(
    (e: React.PointerEvent<SVGRectElement>) => {
      if (!drawingMode || drawingKind !== 'zone') return;
      const pt = clientToWorld(svgRef.current, e.clientX, e.clientY);
      if (!pt) return;
      setZoneHover(pt);
    },
    [drawingMode, drawingKind],
  );

  const onZonePointerLeave = useCallback(() => {
    setZoneHover(null);
  }, []);

  const onZonePointerUp = useCallback(
    (e: React.PointerEvent<SVGRectElement>) => {
      if (!drawingMode || drawingKind !== 'zone') return;
      e.stopPropagation();
      const pt = clientToWorld(svgRef.current, e.clientX, e.clientY);
      const down = zoneDownPosRef.current;
      zoneDownPosRef.current = null;
      if (!pt || !down) return;
      // Reject drags — only commit on real taps so a panned pointer
      // doesn't drop a stray corner.
      const moved = Math.hypot(pt.x - down.x, pt.z - down.z);
      if (moved > TAP_TOLERANCE_M) return;

      const now = Date.now();
      const last = lastTapRef.current;
      const isDoubleTap =
        last !== null &&
        now - last.ts <= DOUBLE_TAP_MS &&
        Math.hypot(pt.x - last.x, pt.z - last.z) <= DOUBLE_TAP_TOLERANCE_M;

      if (isDoubleTap) {
        // Close polygon. Note we don't append the double-tap point —
        // it's a "done" signal, not a vertex.
        const corners = polygonPtsRef.current;
        polygonPtsRef.current = [];
        lastTapRef.current = null;
        setPolygonCorners([]);
        setDrawingPreview(null);
        finishZonePolygon(corners);
        return;
      }

      // Single tap → append corner + refresh preview.
      const nextCorners = [...polygonPtsRef.current, pt];
      polygonPtsRef.current = nextCorners;
      setPolygonCorners(nextCorners);
      lastTapRef.current = { ts: now, x: pt.x, z: pt.z };
      setDrawingPreview(buildZonePreview(polygonPtsRef.current));
    },
    [drawingMode, drawingKind, finishZonePolygon, buildZonePreview],
  );

  // Discard in-progress polygon when the coach leaves zone mode (tool
  // change, drawing mode off, etc.) — otherwise the next zone session
  // would pick up stale corners.
  useEffect(() => {
    if (drawingKind !== 'zone' || !drawingMode) {
      polygonPtsRef.current = [];
      lastTapRef.current = null;
      zoneDownPosRef.current = null;
      setPolygonCorners([]);
      setZoneHover(null);
      setDrawingPreview((p) => (p?.kind === 'zone' ? null : p));
    }
  }, [drawingKind, drawingMode]);

  // Keyboard polish for the zone tool — Escape cancels the in-progress
  // polygon; Backspace removes the most recent corner so coaches can
  // fix a mistap without restarting. Listener attaches only when zone
  // mode is active so the keys keep their normal behaviour elsewhere.
  useEffect(() => {
    if (drawingKind !== 'zone' || !drawingMode) return undefined;
    const onKey = (e: KeyboardEvent) => {
      // Don't fight form fields if focus is somewhere typeable.
      const t = e.target as HTMLElement | null;
      if (
        t &&
        (t.tagName === 'INPUT' ||
          t.tagName === 'TEXTAREA' ||
          t.isContentEditable)
      ) {
        return;
      }
      if (e.key === 'Escape') {
        if (polygonPtsRef.current.length === 0) return;
        e.preventDefault();
        polygonPtsRef.current = [];
        lastTapRef.current = null;
        setPolygonCorners([]);
        setDrawingPreview(null);
        return;
      }
      if (e.key === 'Backspace') {
        if (polygonPtsRef.current.length === 0) return;
        e.preventDefault();
        const next = polygonPtsRef.current.slice(0, -1);
        polygonPtsRef.current = next;
        setPolygonCorners(next);
        // After popping a corner the "last tap" anchor for double-tap
        // detection no longer matches a placed corner — clear it so
        // the next tap doesn't fire as a close.
        lastTapRef.current = null;
        setDrawingPreview(
          next.length === 0 ? null : buildZonePreview(next),
        );
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawingKind, drawingMode, buildZonePreview]);

  // Shared point list for all analytical overlays. `showAway` is a
  // VISUAL toggle (de-clutter the pitch); the overlays should reflect
  // the actual game state regardless, so we always feed both teams
  // into the compute path. Hiding away tokens must not silently
  // change what home's voronoi / pitch control / passnet say.
  const overlayPoints = useMemo(() => {
    return [
      ...homeSpots.map((s) => ({ x: s.x, z: s.z, team: 'home' as const, role: s.role })),
      ...awaySpots.map((s) => ({ x: s.x, z: s.z, team: 'away' as const, role: s.role })),
    ];
  }, [homeSpots, awaySpots]);

  // Voronoi cells — recompute only when overlays.active includes
  // 'voronoi' AND spot positions / focus change. `voronoiFocus`
  // filters which team's points enter the compute so the chosen
  // team's cells expand to fill the whole pitch when the opposition
  // is excluded from the calculation.
  const voronoiCells = useMemo(() => {
    if (!overlays.active.includes('voronoi')) return [];
    const points =
      voronoiFocus === 'home'
        ? overlayPoints.filter((p) => p.team === 'home')
        : voronoiFocus === 'away'
        ? overlayPoints.filter((p) => p.team === 'away')
        : overlayPoints;
    return computeVoronoiCells(points, FIELD_LENGTH, FIELD_WIDTH);
  }, [overlays.active, overlayPoints, voronoiFocus]);

  const passNetEdges = useMemo(() => {
    if (!overlays.active.includes('passNet')) return [];
    return computePassNetwork(overlayPoints);
  }, [overlays.active, overlayPoints]);

  const teamShapeBands = useMemo(() => {
    if (!overlays.active.includes('teamShape')) return [];
    return computeTeamShapeBands(overlayPoints, FIELD_LENGTH);
  }, [overlays.active, overlayPoints]);

  // Vertical + horizontal team-stretch gauges. Always computed (no
  // toggle) — the chips render `—` when there aren't enough
  // outfielders, otherwise show `XX m` per axis.
  const homeCompactness = useMemo(
    () => computeTeamCompactness(homeSpots),
    [homeSpots],
  );
  const awayCompactness = useMemo(
    () => computeTeamCompactness(awaySpots),
    [awaySpots],
  );

  const pitchControlGrid = useMemo(() => {
    if (!overlays.active.includes('pitchControl')) return null;
    return computePitchControl(overlayPoints, FIELD_LENGTH, FIELD_WIDTH);
  }, [overlays.active, overlayPoints]);

  // Heatmap is filtered to the home team. Blending both teams into
  // one density field is nonsense — a coach analysing their team's
  // shape would see opposition contamination. The underlying compute
  // function accepts an `opts.team` filter precisely for this reason.
  const heatmapGrid = useMemo(() => {
    if (!overlays.active.includes('heatmap')) return null;
    return computeHeatmap(overlayPoints, FIELD_LENGTH, FIELD_WIDTH, {
      team: 'home',
    });
  }, [overlays.active, overlayPoints]);

  // Offside + defensive shape need an `isGoalkeeper` flag — derived
  // from the role label since the 'GK' slot in every formation maps
  // to the goalkeeper.
  const offsideInputs = useMemo(() => {
    return overlayPoints.map((p) => ({
      x: p.x,
      z: p.z,
      team: p.team,
      isGoalkeeper: p.role === 'GK',
    }));
  }, [overlayPoints]);

  const offsideLines = useMemo(() => {
    if (!overlays.active.includes('offsideLine')) {
      return { homeLineZ: null, awayLineZ: null };
    }
    return computeOffsideLines(offsideInputs);
  }, [overlays.active, offsideInputs]);

  const defensiveShapes = useMemo(() => {
    if (!overlays.active.includes('defensiveShape')) {
      return { home: { hull: [] }, away: { hull: [] } };
    }
    return {
      home: computeDefensiveShape(offsideInputs, 'home'),
      away: computeDefensiveShape(offsideInputs, 'away'),
    };
  }, [overlays.active, offsideInputs]);

  // When walkthrough is playing OR has scrubbed off zero, derive token
  // positions from the keyframe sample map. Falls back to live spots
  // when no override exists for a token key.
  const walkthroughActive =
    hasKeyframes && (walkthrough.isPlaying || walkthrough.progress > 0);
  const tokenSpotFor = useCallback(
    (key: string, fallback: PlayerSpot): PlayerSpot => {
      if (!walkthroughActive) return fallback;
      const sampled = walkthrough.positions.get(key);
      if (!sampled) return fallback;
      return { ...fallback, x: sampled.x, z: sampled.z };
    },
    [walkthroughActive, walkthrough.positions],
  );

  return (
    <div className="t2d-root tactics-stage" data-testid="tactics-board-2d">
      {/* Top-right tactic actions menu — copy / paste / save / load.
          Floats over the board courtesy of .tactics-stage's
          position: relative. */}
      <TacticMenu
        onCopy={async () => {
          const snap = {
            v: 5 as const,
            homeFormation: home,
            awayFormation: away,
            homeSpots,
            awaySpots,
            arrows,
            zones,
            coachingPoints,
            drawings: drawings as unknown as TacticsLayoutDrawings[],
            saved_at: savedAt ?? new Date().toISOString(),
          };
          const code = encodeTacticCode(snap);
          try {
            await navigator.clipboard?.writeText(code);
          } catch {
            // No clipboard permission — silent. Future: surface a toast.
          }
        }}
        onPaste={async () => {
          let code: string | null = null;
          try {
            code = (await navigator.clipboard?.readText()) ?? null;
          } catch {
            code = null;
          }
          if (!code) return;
          const parsed = decodeTacticCode(code);
          if (!parsed) return;
          setHome(parsed.homeFormation);
          setAway(parsed.awayFormation);
          setHomeSpots(parsed.homeSpots);
          setAwaySpots(parsed.awaySpots);
          setArrows(parsed.arrows);
          if (parsed.zones) setZones(parsed.zones);
          if (parsed.coachingPoints) setCoachingPoints(parsed.coachingPoints);
          if (parsed.drawings) {
            setDrawings(parsed.drawings as unknown as TacticsDrawing[]);
          }
        }}
        onSave={(name) => {
          savePattern({
            name,
            phaseOfPlay: 'attacking',
            formation: home,
            layout: {
              v: 5,
              homeFormation: home,
              awayFormation: away,
              homeSpots,
              awaySpots,
              arrows,
              zones,
              coachingPoints,
              playerRoles,
              teamTactics: teamTactics as TacticsLayout['teamTactics'],
              setPieces: setPieces as unknown as TacticsLayout['setPieces'],
              drawings: drawings as unknown as TacticsLayout['drawings'],
              keyframes: layoutKeyframes,
              saved_at: savedAt ?? new Date().toISOString(),
            },
          });
        }}
        onLoad={() => setPatternsOpen(true)}
      />
      <div className="t2d-stage" data-view={view}>
        <PitchSvg
          svgRef={svgRef}
          onPointerDown={onSvgPointerDown}
          homeLabel={homeLabel}
          homeFormation={home}
          awayLabel={showAway ? awayLabel : undefined}
          awayFormation={showAway ? away : undefined}
          region={pitchCropped && setPieceRegion ? setPieceRegion : undefined}
          panEnabled={mode === 'move' && !drawingMode}
        >
          {/* Phase 3 zone layer — drawn first so arrows + tokens render on
              top of any coloured zones. ZoneLayer2D is memoised + returns
              null when there are no zones, so v=1 layouts pay nothing. */}
          <ZoneLayer2D zones={zones} />
          {/* Wave B / C analytical overlays — sit above pitch markings,
              below arrows / tokens / drawings. Each only renders when
              its kind is toggled active; layers also early-exit on
              empty inputs. Render order: density fields first (heatmap,
              pitchControl), then territory (voronoi), then graph /
              shape annotations (passNet, defensiveShape, offsideLine)
              so the dashed offside line stays crisp on top. */}
          {overlays.active.includes('heatmap') && heatmapGrid && (
            <HeatmapLayer2D
              grid={heatmapGrid}
              opacity={overlays.opacity.heatmap}
            />
          )}
          {overlays.active.includes('pitchControl') && pitchControlGrid && (
            <PitchControlLayer2D
              grid={pitchControlGrid}
              opacity={overlays.opacity.pitchControl}
            />
          )}
          {overlays.active.includes('voronoi') && (
            <VoronoiLayer2D
              cells={voronoiCells}
              opacity={overlays.opacity.voronoi}
              // When focus is single-team, compute already filtered the
              // points so every cell is the chosen team — render them
              // all regardless of the master opp toggle.
              showAway={voronoiFocus !== 'both' ? true : showAway}
            />
          )}
          {overlays.active.includes('halfSpaces') && (
            <HalfSpacesLayer2D opacity={overlays.opacity.halfSpaces} />
          )}
          {overlays.active.includes('posPlayGrid') && (
            <PositionalPlayGridLayer2D opacity={overlays.opacity.posPlayGrid} />
          )}
          {overlays.active.includes('xt') && (
            <XTLayer2D opacity={overlays.opacity.xt} />
          )}
          {overlays.active.includes('teamShape') && (
            <TeamShapeLayer2D
              bands={teamShapeBands}
              opacity={overlays.opacity.teamShape}
              showAway={showAway}
            />
          )}
          {overlays.active.includes('passNet') && (
            <PassNetLayer2D
              edges={passNetEdges}
              opacity={overlays.opacity.passNet}
            />
          )}
          {overlays.active.includes('defensiveShape') && (
            <DefensiveShapeLayer2D
              homeHull={defensiveShapes.home}
              awayHull={defensiveShapes.away}
              opacity={overlays.opacity.defensiveShape}
            />
          )}
          {overlays.active.includes('offsideLine') && (
            <OffsideLineLayer2D
              homeLineZ={offsideLines.homeLineZ}
              awayLineZ={offsideLines.awayLineZ}
              opacity={overlays.opacity.offsideLine}
            />
          )}
          {/* Phase 4 drawings — coach annotation primitives. Rendered
              alongside arrows so the layering follows the order
              they were applied (drawings → arrows → tokens). */}
          <DrawingLayer2D drawings={drawings} />
          {drawingPreview && (
            <DrawingLayer2D drawings={[drawingPreview]} />
          )}
          {arrows.map((a) => (
            <MovementArrow2D key={a.id} arrow={a} reduced={reducedMotion || isPlaying} />
          ))}
          <ArrowDrawLayer2D
            active={mode === 'draw'}
            color={arrowColorHex}
            seedOrigin={arrowSeed?.origin ?? null}
            onSeedConsumed={() => setArrowSeed(null)}
            onArrowComplete={(points) => {
              const id = `arrow-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
              const playerKey = arrowSeed?.playerKey ?? undefined;
              setArrows((prev) => [...prev, { id, points, color: arrowColorHex, playerKey }]);
            }}
            onDragChange={() => undefined}
            svgRef={svgRef}
          />
          {/* Phase 4 drawing capture overlay — sibling to the arrow draw
              layer. Active only when drawing mode is on and a kind has
              been picked. The two capture rects never coexist because
              `mode='draw'` is the arrow mode while `drawingMode` is the
              annotation mode; the toggles below ensure only one is on. */}
          {drawingMode && drawingKind && (
            <rect
              x={-FIELD_LENGTH / 2 - 5}
              y={-FIELD_WIDTH / 2 - 5}
              width={FIELD_LENGTH + 10}
              height={FIELD_WIDTH + 10}
              fill="transparent"
              pointerEvents="all"
              // Zone tool uses a tap-corner-by-corner gesture instead of
              // the drag-to-commit model the other kinds share; route
              // its events to the dedicated handlers. Pointer-move is a
              // no-op for zone (no live edge preview on hover) so we
              // skip it to avoid spurious state churn.
              onPointerDown={
                drawingKind === 'zone' ? onZonePointerDown : onDrawingPointerDown
              }
              onPointerMove={
                drawingKind === 'zone' ? onZonePointerMove : onDrawingPointerMove
              }
              onPointerLeave={
                drawingKind === 'zone' ? onZonePointerLeave : undefined
              }
              onPointerUp={
                drawingKind === 'zone' ? onZonePointerUp : onDrawingPointerUp
              }
              onPointerCancel={
                drawingKind === 'zone' ? onZonePointerUp : onDrawingPointerUp
              }
              data-testid="t2d-drawing-capture-layer"
            />
          )}
          {/* Zone polygon preview — corner dots, polyline + dashed
              guide line to cursor while the coach is mid-polygon.
              Renders only when zone mode is active and there's
              something to show. */}
          {drawingMode && drawingKind === 'zone' &&
            (polygonCorners.length > 0 || zoneHover !== null) && (
              <ZonePolygonOverlay
                corners={polygonCorners}
                hover={zoneHover}
                stroke={drawingStroke}
                strokeWidth={drawingStrokeWidth}
              />
            )}
          {homeSpots.map((spot, i) => {
            const key = `home-${i}`;
            const renderSpot = tokenSpotFor(key, spot);
            return (
              <PlayerToken2D
                key={key}
                playerKey={key}
                spot={renderSpot}
                number={i + 1}
                label={rosterIdentityFor(i, roster[key]).token}
                team="home"
                selected={selectedKey === key}
                mode={mode}
                playbackPos={playbackPositions.get(key) ?? null}
                onSelect={() => handleTokenSelect(key)}
                onDragChange={() => undefined}
                onMove={moveHomeSpot(i)}
                onStartArrowFromToken={(origin, pk) =>
                  setArrowSeed({ origin, playerKey: pk })
                }
                svgRef={svgRef}
              />
            );
          })}
          {showAway &&
            awaySpots.map((spot, i) => {
              const key = `away-${i}`;
              const renderSpot = tokenSpotFor(key, spot);
              return (
                <PlayerToken2D
                  key={key}
                  playerKey={key}
                  spot={renderSpot}
                  number={i + 1}
                  label={rosterIdentityFor(i, roster[key]).token}
                  team="away"
                  selected={selectedKey === key}
                  mode={mode}
                  playbackPos={playbackPositions.get(key) ?? null}
                  onSelect={() => handleTokenSelect(key)}
                  onDragChange={() => undefined}
                  onMove={moveAwaySpot(i)}
                  onStartArrowFromToken={(origin, pk) =>
                    setArrowSeed({ origin, playerKey: pk })
                  }
                  svgRef={svgRef}
                />
              );
            })}
          {/* Coaching-point pins (Phase 3). No active keyframe wired yet
              — Agent D owns the keyframe UI — so we show only the pins
              without a `keyframeId` (general teaching points). Pins
              bound to a keyframe stay hidden until playback engages. */}
          {coachingPoints
            .filter((p) => !p.keyframeId)
            .map((p) => (
              <CoachingPointPin key={p.id} point={p} surface="svg" />
            ))}
        </PitchSvg>
      </div>

      <HudShell liveMessage={selectionAnnouncement}>
        {/* Sister-surface nav — jump to Session / Set Pieces / Match Day. */}
        <SurfacePills />
        <HudRow
          label="Mode"
          trailing={persistKey ? <SavedAgo savedAt={savedAt} /> : undefined}
        >
          <HudChip active={mode === 'move'} onClick={() => setMode('move')}>
            Move
          </HudChip>
          <HudChip active={mode === 'draw'} onClick={() => setMode('draw')}>
            Draw arrow
          </HudChip>
          {mode === 'draw' && (
            <>
              <HudHint>
                drag from a player to bind a run · drag on grass for a free arrow
              </HudHint>
              <span className="thud__label" style={{ marginLeft: 8 }}>Colour</span>
              {(['home', 'away', 'neutral'] as const).map((c) => (
                <HudChip
                  key={c}
                  team={c === 'away' ? 'away' : undefined}
                  active={arrowColor === c}
                  onClick={() => setArrowColor(c)}
                >
                  {c}
                </HudChip>
              ))}
              {arrows.length > 0 && (
                <>
                  <HudChip onClick={() => setArrows((prev) => prev.slice(0, -1))}>
                    Undo
                  </HudChip>
                  <HudChip onClick={() => setArrows([])}>Clear arrows</HudChip>
                </>
              )}
            </>
          )}
          {mode === 'move' && (
            <>
              <HudHint>tap a token + drag to reposition</HudHint>
              <HudChip
                onClick={() => {
                  setHomeSpots(projectFormation(home, 'home'));
                  setAwaySpots(projectFormation(away, 'away'));
                  setArrows([]);
                  setZones([]);
                  setCoachingPoints([]);
                  setDrawings([]);
                  setPlayerRoles({});
                  setSelectedKey(null);
                  if (persistKey) {
                    clearTacticsLayout(persistKey);
                    setSavedAt(null);
                  }
                }}
              >
                Reset
              </HudChip>
            </>
          )}
        </HudRow>
        <HudRow label="View">
          {VIEW_PRESETS.map((preset) => (
            <HudChip
              key={preset.id}
              active={view === preset.id}
              onClick={() => setView(preset.id)}
            >
              {preset.label}
            </HudChip>
          ))}
          <HudChip
            active={showAway}
            ariaLabel={showAway ? 'Hide opponent team' : 'Show opponent team'}
            onClick={() => setShowAway((s) => !s)}
          >
            {showAway ? 'Opponent · on' : 'Opponent · off'}
          </HudChip>
          {/* Set-piece field-slice toggle — only shown once a set piece
              has been applied. Flips PitchSvg between the cropped slice
              and the full pitch. */}
          {setPieceRegion && (
            <HudChip
              active={pitchCropped}
              ariaLabel={
                pitchCropped
                  ? 'Show the full pitch'
                  : 'Crop to the set-piece slice'
              }
              onClick={() => setPitchCropped((c) => !c)}
            >
              {pitchCropped ? 'Set piece · slice' : 'Set piece · full pitch'}
            </HudChip>
          )}
          {/* Patterns chip moved into the Design row's Library
              dropdown — see <LibraryMenu /> below. */}
        </HudRow>

        {/* Compactness gauges — vertical (front-to-back) and horizontal
            (width) team stretch. Computed off outfielder spots only. */}
        <HudRow label="Stats">
          <CompactnessChips
            home={homeCompactness}
            away={awayCompactness}
            showAway={showAway}
          />
        </HudRow>

        {/* Phase 4 — DESIGN row. Each chip is either a drawer toggle or
            a mode toggle. The undo/redo chips live alongside the design
            chips so coaches can recover from any of the new surfaces. */}
        <HudRow label="Design">
          <LibraryMenu
            anyOpen={tacticsPanelOpen || setPieceOpen || patternsOpen}
            onOpenPatterns={() => setPatternsOpen(true)}
            onOpenTeamTactics={() => {
              setTacticsPanelTeam('home');
              if (!teamTactics) setTeamTactics(defaultBundle());
              setTacticsPanelOpen(true);
            }}
            onOpenSetPieces={() => navigate('/setpieces')}
          />
          <HudChip
            active={overlayPanelOpen}
            ariaLabel="Toggle analytical overlays"
            onClick={() => setOverlayPanelOpen((o) => !o)}
          >
            Overlays
          </HudChip>
          <HudChip
            active={briefsOpen}
            ariaLabel="Open player briefs"
            onClick={() => setBriefsOpen((o) => !o)}
          >
            Briefs{playerBriefsStale && Object.keys(playerBriefs).length > 0 ? ' •' : ''}
          </HudChip>
          {/* Design / Phase + + Keyframe chips removed per coach
              feedback — the walkthrough authoring loop they enabled
              was confusing on its own and interfered with regular
              drags via `tokenSpotFor` (when walkthrough.progress
              drifts > 0, the displayed token reads from the
              keyframe sample map instead of homeSpots, so a drag
              committed-but-invisible). Keyframe state + editMode
              state retained so Brain Bar's sequence-commit path
              still wires through. Re-enable with a more discoverable
              surface (e.g. a "Build a sequence" disclosure) when
              walkthrough authoring is the focus. */}
          {/* Copy code / Paste code chips moved to the top-right
              TacticMenu popover (see <TacticMenu /> below). Joins
              Save to library + Load from library under one
              "Tactic ▾" button so the Design row stays focused on
              authoring tools. */}
          <HudChip
            ariaLabel="Mirror layout left to right"
            onClick={() => {
              // Build a snapshot of the coordinate-bearing state, mirror
              // across the x axis, then write back via the existing
              // setters. The `TacticsLayout.drawings` slot is
              // permissively typed (`Record<string, unknown>` & {id})
              // while the board state uses the concrete `TacticsDrawing`
              // from `./drawings`; cast through `unknown` to bridge.
              const snap = {
                v: 4 as const,
                homeFormation: home,
                awayFormation: away,
                homeSpots,
                awaySpots,
                arrows,
                zones,
                coachingPoints,
                drawings: drawings as unknown as TacticsLayoutDrawings[],
                saved_at: savedAt ?? new Date().toISOString(),
              };
              const flipped = mirrorTacticsLayout(snap, 'x');
              setHomeSpots(flipped.homeSpots);
              setAwaySpots(flipped.awaySpots);
              setArrows(flipped.arrows);
              if (flipped.zones) setZones(flipped.zones);
              if (flipped.coachingPoints) setCoachingPoints(flipped.coachingPoints);
              if (flipped.drawings) {
                setDrawings(flipped.drawings as unknown as TacticsDrawing[]);
              }
            }}
          >
            Mirror
          </HudChip>
          {/* "Roles" chip removed per coach feedback — was an
              open-play named-role picker (false-9 / ball-playing /
              etc.). The role data still flows through layout.playerRoles
              (Brain Bar + famous tactics both write it; the brief
              engine reads it), so existing role assignments survive.
              Set-piece role-per-player (C4) will replace this surface
              when it lands. */}
          <HudChip
            active={drawingMode}
            ariaLabel="Toggle drawing tools"
            onClick={() => {
              setDrawingMode((on) => {
                const next = !on;
                if (!next) setDrawingKind(null);
                return next;
              });
            }}
          >
            Draw
          </HudChip>
          <HudChip
            ariaLabel="Undo last change"
            onClick={() => history.undo()}
          >
            Undo
          </HudChip>
          <HudChip
            ariaLabel="Redo last undone change"
            onClick={() => history.redo()}
          >
            Redo
          </HudChip>
        </HudRow>

        {/* Inline overlay panel — chip-row sized, slots beneath the
            design row when toggled. No modal: it's a passive
            visualisation toggle so the coach can keep tweaking the
            pitch alongside it. */}
        {overlayPanelOpen && (
          <div className="thud__row" data-testid="t2d-overlay-panel">
            <span className="thud__label">OVERLAYS</span>
            <div className="thud__row__main">
              <OverlayToggles state={overlays} onChange={setOverlays} />
              {/* Spacing focus — only meaningful when Spacing is on.
                  3-way chip group: Both / Home / Away. Drives the
                  voronoi compute, so single-team focus expands the
                  chosen team's cells across the whole pitch. */}
              {overlays.active.includes('voronoi') && (
                <>
                  <span className="thud__label">FOCUS</span>
                  <HudChip
                    active={voronoiFocus === 'both'}
                    ariaLabel="Spacing focus: both teams"
                    onClick={() => setVoronoiFocus('both')}
                  >
                    Both
                  </HudChip>
                  <HudChip
                    active={voronoiFocus === 'home'}
                    ariaLabel="Spacing focus: home only"
                    onClick={() => setVoronoiFocus('home')}
                    team="home"
                  >
                    Home only
                  </HudChip>
                  <HudChip
                    active={voronoiFocus === 'away'}
                    ariaLabel="Spacing focus: away only"
                    onClick={() => setVoronoiFocus('away')}
                    team="away"
                  >
                    Away only
                  </HudChip>
                </>
              )}
            </div>
          </div>
        )}

        {/* Drawing toolbar — sticky strip above the HUD when drawing
            mode is engaged. Lives in the HUD shell so it stays
            anchored as the coach scrolls the pitch. */}
        {drawingMode && (
          <DrawingToolbar
            activeKind={drawingKind}
            onSelectKind={setDrawingKind}
            stroke={drawingStroke}
            onStrokeChange={setDrawingStroke}
            strokeWidth={drawingStrokeWidth}
            onStrokeWidthChange={setDrawingStrokeWidth}
            dash={drawingDash}
            onDashChange={setDrawingDash}
            arrowHead={drawingArrowHead}
            onArrowHeadChange={setDrawingArrowHead}
          />
        )}

        {boundArrowCount > 0 && (
          <HudRow label="Runs">
            <RunPlaybackControls
              isPlaying={isPlaying}
              progress={playbackProgress}
              boundArrowCount={boundArrowCount}
              onPlayPause={() => {
                if (isPlaying) {
                  setIsPlaying(false);
                  return;
                }
                if (playbackProgress >= 1) setPlaybackProgress(0);
                setIsPlaying(true);
              }}
              onStop={() => {
                setIsPlaying(false);
                setPlaybackProgress(0);
              }}
              onScrub={(next) => {
                setIsPlaying(false);
                setPlaybackProgress(next);
              }}
            />
            <span className="thud__hint" aria-hidden="true">
              {playbackPct}%
            </span>
          </HudRow>
        )}
        <HudRow label="Home">
          <FormationDropdown
            current={home}
            customs={listCustomFormations()}
            onChange={(codeOrId) => handleApplyFormation(codeOrId, 'home')}
            onSaveCustom={(name) => {
              saveCustomFormation({
                name,
                slots: homeSpots.map((s) => ({ role: s.role, x: s.x, z: s.z })),
              });
            }}
          />
        </HudRow>
        {showAway && (
          <HudRow label="Away">
            <FormationDropdown
              team="away"
              current={away}
              customs={listCustomFormations()}
              onChange={(codeOrId) => handleApplyFormation(codeOrId, 'away')}
              onSaveCustom={(name) => {
                saveCustomFormation({
                  name,
                  slots: awaySpots.map((s) => ({ role: s.role, x: s.x, z: s.z })),
                });
              }}
            />
          </HudRow>
        )}

        {/* Phase 2/3 walkthrough — only mounts when the layout carries
            ≥2 keyframes. WalkthroughControls handles the single-snapshot
            no-op internally, but we gate here so the HUD row doesn't
            consume layout space when there's nothing to walk. */}
        {hasKeyframes && (
          <WalkthroughControls
            keyframes={layoutKeyframes}
            branches={layoutBranches}
            voiceovers={layoutVoiceovers}
            isPlaying={walkthrough.isPlaying}
            progress={walkthrough.progress}
            activeKeyframeId={walkthrough.activeKeyframeId}
            activeBranchId={walkthrough.activeBranchId}
            onPlay={walkthrough.play}
            onPause={walkthrough.pause}
            onReset={walkthrough.reset}
            onScrub={walkthrough.setProgress}
            onSelectBranch={walkthrough.setBranch}
            ttsEnabled={walkthroughTtsEnabled}
            onToggleTts={() => setWalkthroughTtsEnabled((on) => !on)}
          />
        )}
      </HudShell>

      {slotPicker && (
        <PositionSlotPicker
          currentSlot={slotPicker.currentSlot}
          suggested={slotPicker.suggested}
          onPick={(slot) => {
            // Commit the chosen slot to the spot the picker is bound to.
            const [side, idxStr] = slotPicker.key.split('-');
            const idx = Number(idxStr);
            if (side === 'home') {
              setHomeSpots((prev) =>
                prev.map((s, i) => (i === idx ? { ...s, role: slot } : s)),
              );
            } else if (side === 'away') {
              setAwaySpots((prev) =>
                prev.map((s, i) => (i === idx ? { ...s, role: slot } : s)),
              );
            }
            setSlotPicker(null);
          }}
          onClose={() => setSlotPicker(null)}
        />
      )}

      <PatternDrawer
        open={patternsOpen}
        onClose={() => setPatternsOpen(false)}
        onSelectPattern={handleSelectPattern}
      />

      {/* Phase 4 — drawer / picker mounts. Each component owns its own
          open-state gating + portal-style overlay; we render them
          unconditionally so React's reconciliation doesn't tear down
          the dialog DOM between opens (which would lose focus). */}
      <FormationLibraryDrawer
        open={formationLibOpen}
        onClose={() => setFormationLibOpen(false)}
        // formationLibTeam removed when the open-trigger chip went;
        // default to 'home' until/unless a future surface re-introduces
        // the team picker.
        onApply={handleApplyFormation}
        currentTeam="home"
      />

      <PlayerBriefsDrawer
        open={briefsOpen}
        onClose={() => setBriefsOpen(false)}
        briefs={playerBriefs}
        spots={homeSpots}
        side="home"
        layoutStale={playerBriefsStale}
        onRegenerate={handleRegenerateBriefs}
        onEdit={handleEditBrief}
        busy={briefsBusy}
        history={playerBriefHistory}
        onRestore={handleRestoreBrief}
        roster={roster}
        onRosterEdit={(key, next) => {
          setRoster((prev) => {
            const noName = next.name === undefined || next.name === '';
            const noNumber = next.number === undefined;
            if (noName && noNumber) {
              // Empty entry → drop the key entirely so the store
              // doesn't accumulate {} placeholders.
              const { [key]: _drop, ...rest } = prev;
              return rest;
            }
            return { ...prev, [key]: next };
          });
        }}
        onImportRoster={() => {
          // Fill home roster slots from the team's existing players in
          // index order. No smart position matching in v1 — coach
          // re-arranges via the per-card inputs. Only fills the first
          // homeSpots.length names; extras are ignored, deficits left
          // blank.
          const players = loadLocalPlayers();
          if (players.length === 0) {
            window.alert(
              'No players found in the team roster. Add some on the Players page first.',
            );
            return;
          }
          setRoster((prev) => {
            const next = { ...prev };
            for (let i = 0; i < homeSpots.length; i++) {
              const player = players[i];
              if (!player) break;
              const entry = next[`home-${i}`] ?? {};
              next[`home-${i}`] = {
                ...entry,
                name: player.name,
              };
            }
            return next;
          });
        }}
        // persistKey shape: "tactics-3d-{sessionId}" — strip the prefix
        // so the print-friendly page route receives just the sessionId.
        rosterHref={
          persistKey?.startsWith('tactics-3d-')
            ? `/tactics/${persistKey.slice('tactics-3d-'.length)}/briefs`
            : undefined
        }
      />

      {tacticsPanelOpen && teamTactics && (
        <TeamTacticsPanel
          bundle={teamTactics}
          team={tacticsPanelTeam}
          onChange={(next) => {
            // Coach-driven edit — flag so the spatial-slider effect
            // re-projects spots on the next render. Brain commits +
            // pattern recalls go through the listener path which
            // doesn't set this flag, so loaded layouts aren't
            // re-transformed twice.
            teamShapeChangedRef.current = true;
            setTeamTactics(next);
          }}
          onClose={() => setTacticsPanelOpen(false)}
        />
      )}

      <SetPieceDrawer
        open={setPieceOpen}
        onClose={() => setSetPieceOpen(false)}
        onApply={handleApplySetPiece}
        onSaveCurrentAs={handleSaveSetPiece}
        // Pre-select the tab from the `?type=` deep-link param when the
        // drawer was opened via /setpieces "+ Create".
        initialType={initialSetPieceType}
      />

      {roleTarget && (
        <RoleInstructionsPicker
          tokenKey={roleTarget.key}
          slot={roleTarget.slot}
          current={playerRoles[roleTarget.key]}
          onChange={(next) =>
            setPlayerRoles((prev) => ({ ...prev, [roleTarget.key]: next }))
          }
          onClose={() => {
            setRoleTarget(null);
            setRoleMode(false);
          }}
        />
      )}
    </div>
  );
}
